import restClient from "api/restClient";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { getUserData } from "containers/Auth/Auth.selectors";
import { all, call, delay, put, select, takeEvery } from "redux-saga/effects";
import { mapScreensToPaths } from "utils/RouterManager/utils";
import { capitalizeFirstLetterRestLowercase } from "utils/utils";
import { actions as bundlesActions } from "./Bundles.reducer";

function* fetchSingleBundleRepository({ bundleRepositoryName, bundleRepositoryId }) {
    try {
        const response = yield restClient.get(
            `/bundle_repository/${bundleRepositoryName}/list?by=name`
        );
        yield put({
            type: bundlesActions.FETCH_BUNDLES_SUCCESSFUL,
            response: { [bundleRepositoryId]: response?.data },
        });
    } catch (error) {
        yield put({
            type: bundlesActions.FETCH_BUNDLES_FAILED,
        });

        NotificationService.error(
            <RestErrorNotification
                error={error}
                titleOverride={`Failed to fetch bundle list for ${bundleRepositoryName} repository`}
            />
        );
    }
}

function* fetchBundleRepository({ bundleRepositoryData, bundleRepositoryId }) {
    const bundleRepositoryName = bundleRepositoryData[bundleRepositoryId];
    yield fetchSingleBundleRepository({ bundleRepositoryName, bundleRepositoryId });
}

/**
 * Fetch Bundles
 * @param  {Object} promise
 * @return {Object} response from API
 */
export function* fetchBundles({ promise, bundleRepositoryData }) {
    const actionList = [];
    for (const bundleRepositoryId in bundleRepositoryData) {
        actionList.push(call(fetchBundleRepository, { bundleRepositoryData, bundleRepositoryId }));
    }
    yield all(actionList);
    yield put({
        type: bundlesActions.BUNDLE_ACTION_FINISHED,
    });
    yield call(promise.resolve);
}

const queryParams = {
    params: {
        allow_deleted: true,
    },
};

const uninstallQueryParams = (resource) => ({
    bundle_id: resource?.resourceData.id,
});

function* singleBundleAction({ promise, action, resource, isBulk, params }) {
    const bundleRepositoryName = isBulk
        ? resource?.resourceData?.repository_name
        : resource?.resourceData?.bundle_repository_id_bundle_repository?.name;
    const apiResult = yield restClient.post(
        `/bundle_repository/${bundleRepositoryName}/${action}/${resource?.resourceData.key}?by=name`,
        params
    );
    yield call(promise.resolve, apiResult);
}

function* catchSingleBundleAction({ promise, error }) {
    yield call(promise.reject, error?.response?.data || error);
}

function* bulkBundleAction({ promise, resource, action, params, actionLabel: actionLabelOverride }) {
    const actionLabel = actionLabelOverride || capitalizeFirstLetterRestLowercase(action);
    try {
        yield put({
            type: bundlesActions.SINGLE_BUNDLE_ACTION_STARTED,
            name: resource?.resourceData.name,
        });
        yield singleBundleAction({ promise, action, resource, isBulk: true, params });
        NotificationService.info(
            `${actionLabel} of "${resource?.resourceData.name}" bundle started`
        );
        yield put({
            type: bundlesActions.SINGLE_BUNDLE_ACTION_FINISHED,
            name: resource?.resourceData.name,
        });
    } catch (error) {
        NotificationService.error(
            <RestErrorNotification
                titleOverride={`${actionLabel} of "${resource?.resourceData.name}" bundle failed`}
                error={error}
            />
        );
        catchSingleBundleAction({ promise, error });
        yield put({
            type: bundlesActions.SINGLE_BUNDLE_ACTION_FINISHED,
            name: resource?.resourceData.name,
        });
    }
}

function* finishBulkAction({ afterAction }) {
    yield put({
        type: bundlesActions.BUNDLE_ACTION_FINISHED,
    });
    afterAction();
}

const installProps = { action: "install", params: queryParams };
const uninstallProps = { action: "uninstall", params: uninstallQueryParams };
const releaseProps = { action: "release", params: queryParams };
// Purge = remove the released bundle from the REMOTE repository (delete_bundle);
// distinct from uninstall (local) and release (publish). Backend verb: `delete`.
const purgeProps = { action: "delete", params: queryParams, actionLabel: "Purge" };
/**
 * Uninstall bundles
 */
export function* uninstallBundle({ promise, resources, afterAction }) {
    for (const resource of resources) {
        yield bulkBundleAction({
            promise,
            resource,
            ...uninstallProps,
        });
    }
    yield finishBulkAction({ afterAction });
}

/**
 * Install or update bundles
 */
export function* installBundle({ promise, resources, afterAction }) {
    for (const resource of resources) {
        yield bulkBundleAction({
            promise,
            resource,
            ...installProps,
        });
    }
    yield finishBulkAction({ afterAction });
}

/**
 * Release bundles
 */
export function* releaseBundle({ promise, resources, afterAction }) {
    for (const resource of resources) {
        yield bulkBundleAction({
            promise,
            resource,
            ...releaseProps,
        });
    }
    yield finishBulkAction({ afterAction });
}

export function* uninstallSingleBundle({ promise, resource, history }) {
    try {
        yield singleBundleAction({
            promise,
            resource,
            isBulk: false,
            ...uninstallProps,
        });
        const currentUser = yield select(getUserData);
        const currentUserId = currentUser?.identity_id;
        const triggeredByUserId = resource?.userData?.identity_id;
        if (currentUserId === triggeredByUserId) {
            const workspaceLogLink = mapScreensToPaths.bundleManager(currentUser?.workspace_id);
            history.push(workspaceLogLink);
        }
    } catch (error) {
        yield catchSingleBundleAction({ promise, error });
    }
}
export function* installSingleBundle({ promise, resource }) {
    try {
        yield singleBundleAction({
            promise,
            resource,
            isBulk: false,
            ...installProps,
        });
    } catch (error) {
        yield catchSingleBundleAction({ promise, error });
    }
}
export function* releaseSingleBundle({ promise, resource }) {
    try {
        yield singleBundleAction({
            promise,
            resource,
            isBulk: false,
            ...releaseProps,
        });
    } catch (error) {
        yield catchSingleBundleAction({ promise, error });
    }
}

/**
 * Purge bundles from the remote repository (bulk)
 */
export function* purgeBundle({ promise, resources, afterAction }) {
    for (const resource of resources) {
        yield bulkBundleAction({
            promise,
            resource,
            ...purgeProps,
        });
    }
    yield finishBulkAction({ afterAction });
}
export function* purgeSingleBundle({ promise, resource }) {
    try {
        yield singleBundleAction({
            promise,
            resource,
            isBulk: false,
            ...purgeProps,
        });
    } catch (error) {
        yield catchSingleBundleAction({ promise, error });
    }
}

/**
 * Git maintenance (gc/repack/prune) on a git-enabled bundle's bare repo.
 *
 * The backend runs the maintenance as a DETACHED background task and returns
 * 200 immediately (an initial status with `accepted: true`, or the existing
 * status with `already_running: true` when a run already owns the lock). We
 * then poll `GET bundle/{id}/git/maintenance` until the run reaches a terminal
 * state (DONE/ERROR) and surface the outcome (reclaimed bytes / error) as a
 * notification. States: STARTING -> LOCKING -> RUNNING -> DONE | ERROR.
 */
const GIT_MAINTENANCE_NON_TERMINAL_STATES = new Set(["STARTING", "LOCKING", "RUNNING"]);
const GIT_MAINTENANCE_POLL_INTERVAL_MS = 5000;
// A full gc/repack on a large bare can take many minutes; cap the poll loop so a
// stuck/very-long run does not poll forever. If the cap is hit we stop silently
// (the user can re-open the bundle and GET the status again).
const GIT_MAINTENANCE_MAX_POLLS = 360; // ~30 min at 5s

function formatBytes(bytes) {
    const n = Number(bytes) || 0;
    if (n <= 0) {
        return "0 B";
    }
    const units = ["B", "KiB", "MiB", "GiB", "TiB"];
    const exp = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
    const value = n / 1024 ** exp;
    return `${value.toFixed(exp === 0 ? 0 : 1)} ${units[exp]}`;
}

function* pollGitMaintenance({ id, name }) {
    for (let i = 0; i < GIT_MAINTENANCE_MAX_POLLS; i += 1) {
        yield delay(GIT_MAINTENANCE_POLL_INTERVAL_MS);
        let data;
        try {
            const response = yield restClient.get(`bundle/${id}/git/maintenance`);
            data = response?.data;
        } catch (error) {
            // Transient read failure (e.g. ingress hitting a busy container) —
            // keep polling; a persistent failure just ends at the cap.
            continue;
        }
        if (data && !GIT_MAINTENANCE_NON_TERMINAL_STATES.has(data.state)) {
            if (data.state === "DONE") {
                const reclaimed = formatBytes(data.reclaimed_bytes);
                const dryRunSuffix = data.dry_run ? " (dry run)" : "";
                NotificationService.success(
                    `Git maintenance of "${name}" finished — reclaimed ${reclaimed}${dryRunSuffix}`
                );
            } else if (data.state === "ERROR") {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride={`Git maintenance of "${name}" failed`}
                        error={{ response: { data: { message: data.error } } }}
                    />
                );
            }
            return;
        }
    }
}

export function* gitMaintenanceSingleBundle({ promise, resource, body }) {
    const id = resource?.resourceData?.id;
    const name = resource?.resourceData?.name;
    try {
        const apiResult = yield restClient.post(`bundle/${id}/git/maintenance`, body || {});
        const data = apiResult?.data;
        if (data?.already_running) {
            NotificationService.info(`Git maintenance is already running on "${name}"`);
        } else {
            const label = body?.prune_only ? "Prune (light) maintenance" : "Git maintenance";
            NotificationService.info(`${label} of "${name}" started`);
        }
        // Resolve the action promise now that the run is accepted; the poll loop
        // continues in the background and reports the terminal outcome.
        yield call(promise.resolve, data);
        yield call(pollGitMaintenance, { id, name });
    } catch (error) {
        NotificationService.error(
            <RestErrorNotification
                titleOverride={`Git maintenance of "${name}" could not be started`}
                error={error}
            />
        );
        yield call(promise.reject, error?.response?.data || error);
    }
}

/**
 * Register action to watcher
 */
export default [
    takeEvery(bundlesActions.START_FETCHING_BUNDLES, fetchBundles),
    takeEvery(bundlesActions.START_FETCHING_BUNDLE_REPOSITORY, fetchSingleBundleRepository),
    takeEvery(bundlesActions.START_UNINSTALL_BUNDLES, uninstallBundle),
    takeEvery(bundlesActions.START_INSTALL_BUNDLES, installBundle),
    takeEvery(bundlesActions.START_RELEASE_BUNDLES, releaseBundle),
    takeEvery(bundlesActions.START_RELEASE_SINGLE_BUNDLE, releaseSingleBundle),
    takeEvery(bundlesActions.START_UNINSTALL_SINGLE_BUNDLE, uninstallSingleBundle),
    takeEvery(bundlesActions.START_INSTALL_SINGLE_BUNDLE, installSingleBundle),
    takeEvery(bundlesActions.START_PURGE_BUNDLES, purgeBundle),
    takeEvery(bundlesActions.START_PURGE_SINGLE_BUNDLE, purgeSingleBundle),
    takeEvery(bundlesActions.START_GIT_MAINTENANCE_SINGLE_BUNDLE, gitMaintenanceSingleBundle),
];
